import { FactCheckOutlined } from "@mui/icons-material";
import { Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { decideFefoException, FefoException, loadFefoExceptions } from "./api/fefo";
import { ListRowActions } from "./ListRowActions";
import { operationalStatusLabel } from "./operationalStatus";
import { LabeledInfo } from "./LabeledInfo";

interface Props {
  canExecute: boolean;
  tab: "open" | "closed";
}

export function FefoDerogationQueue({ canExecute, tab }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<FefoException | null>(null);
  const query = useQuery({ queryKey: ["fefo-exceptions"], queryFn: loadFefoExceptions });
  const decision = useMutation({
    mutationFn: ({ exception, value }: { exception: FefoException; value: "APPROVED" | "REJECTED" }) =>
      decideFefoException(exception.id, value,
        value === "APPROVED" ? "Authorized FEFO deviation" : "FEFO deviation rejected",
        exception.version),
    onSuccess: async () => {
      setSelected(null);
      await queryClient.invalidateQueries({ queryKey: ["fefo-exceptions"] });
    }
  });
  const all = query.data ?? [];
  const pending = all.filter(e => ["OPEN", "UNDER_REVIEW"].includes(e.status));
  const completed = all.filter(e => ["APPROVED", "REJECTED", "RESOLVED", "CLOSED"].includes(e.status));
  const visible = tab === "open" ? pending : completed;
  const formatDate = (value: string | null) => value ? new Date(value).toLocaleDateString() : "—";

  return <Fragment>
    {query.isError && <Alert severity="error">{t("quality.derogations.fefoLoadError")}</Alert>}
    {visible.map(e => <Card key={e.id} variant="outlined" sx={{
      position: "relative", borderLeft: 4, borderLeftColor: "secondary.main"
    }}>
      <CardContent>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}
          alignItems={{ sm: "center" }}>
          <FactCheckOutlined color="secondary" />
          <Stack spacing={1.25} sx={{ minWidth: 0, flexGrow: 1 }}>
            <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
              <LabeledInfo label={t("quality.derogationCard.type")} prominent>{t(`dashboard.expiryRisk.exception.${e.exceptionType}`)}</LabeledInfo>
              <LabeledInfo label={t("quality.derogationCard.warehouse")}>{e.warehouseCode}</LabeledInfo>
            </Stack>
            <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}
              sx={{ p: 1.25, bgcolor: "background.default", borderRadius: 1 }}>
              <LabeledInfo label={t("quality.derogationCard.item")} prominent>{e.itemCode}</LabeledInfo>
              <LabeledInfo label={t("quality.derogationCard.description")}>{e.itemName}</LabeledInfo>
              <LabeledInfo label={t("quality.derogationCard.hu")} prominent>{e.handlingUnitNumber ?? "—"}</LabeledInfo>
              <LabeledInfo label={t("quality.derogationCard.lot")}>{e.lotNumber ?? "—"}</LabeledInfo>
              <LabeledInfo label={t("quality.derogationCard.expiry")}>{formatDate(e.expirationDate)}</LabeledInfo>
            </Stack>
            <LabeledInfo label={t("quality.derogationCard.reason")}>{e.requestReason ?? e.reason}</LabeledInfo>
            {e.expectedHandlingUnitNumber && (
              <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                <LabeledInfo label={t("quality.derogationCard.alternativeHu")}>{e.expectedHandlingUnitNumber}</LabeledInfo>
                {e.expectedLotNumber && <LabeledInfo label={t("quality.derogationCard.alternativeLot")}>{e.expectedLotNumber}</LabeledInfo>}
                {e.expectedAvailableQuantity != null && <LabeledInfo label={t("quality.derogationCard.alternativeQuantity")}>{e.expectedAvailableQuantity} {e.expectedUom ?? ""}</LabeledInfo>}
              </Stack>
            )}
          </Stack>
          <ListRowActions actions={
            <Button variant="outlined"
              onClick={() => { decision.reset(); setSelected(e); }}>
              {t("dashboard.expiryRisk.reviewException")}
            </Button>
          } status={
            <Chip label={operationalStatusLabel(e.status)} color={
              ["OPEN", "UNDER_REVIEW"].includes(e.status) ? "warning"
                : ["APPROVED", "RESOLVED", "CLOSED"].includes(e.status)
                  ? "success" : e.status === "REJECTED" ? "error" : "default"
            } />
          } />
        </Stack>
      </CardContent>
    </Card>)}
    <Dialog open={selected !== null} onClose={() => setSelected(null)} fullWidth maxWidth="md">
      {selected && <><DialogTitle><Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
        <Box><Typography variant="h5" fontWeight={900}>{selected.itemCode}</Typography><Typography color="text.secondary">{selected.itemName} · {selected.warehouseCode}</Typography></Box>
        <Chip color="warning" label={operationalStatusLabel(selected.status)} />
      </Stack></DialogTitle><DialogContent dividers><Stack spacing={2}>
        <Alert severity="error" variant="outlined"><b>{t(`dashboard.expiryRisk.exception.${selected.exceptionType}`)}</b><br />{selected.reason}</Alert>
        <Grid container spacing={1.5}><Grid size={{ xs: 12, sm: 6 }}><Card variant="outlined" sx={{ p: 1.5, height: "100%" }}>
          <Typography color="text.secondary">{t("dashboard.expiryRisk.selectedLotHu")}</Typography><Typography fontWeight={850}>{selected.lotNumber ?? "—"} · {selected.handlingUnitNumber ?? "—"}</Typography><Typography variant="body2">{t("dashboard.expiryRisk.expiryDate")}: {formatDate(selected.expirationDate)}</Typography>
        </Card></Grid><Grid size={{ xs: 12, sm: 6 }}><Card variant="outlined" sx={{ p: 1.5, height: "100%" }}>
          <Typography color="text.secondary">{t("dashboard.expiryRisk.shelfLifeAtNeed")}</Typography><Typography fontWeight={850}>{selected.actualRemainingDays ?? "—"} d / {selected.requiredRemainingDays ?? "—"} d</Typography><Typography variant="body2">{t("dashboard.expiryRisk.requiredDate")}: {formatDate(selected.requiredOn)}</Typography>
        </Card></Grid></Grid>
        {selected.exceptionType === "FEFO_SEQUENCE_BYPASS" && <Card variant="outlined" sx={{ p: 1.5, borderLeft: 4, borderLeftColor: "success.main" }}>
          <Typography color="text.secondary">{t("dashboard.expiryRisk.expectedAlternative")}</Typography>
          {selected.expectedHandlingUnitNumber ? <>
            <Typography fontWeight={850}>{selected.expectedLotNumber ?? "—"} · {selected.expectedHandlingUnitNumber}</Typography>
            <Typography variant="body2">{t("dashboard.expiryRisk.expiryDate")}: {formatDate(selected.expectedExpirationDate)}{selected.expectedAvailableQuantity != null ? ` · ${selected.expectedAvailableQuantity} ${selected.expectedUom ?? ""}` : ""}</Typography>
          </> : <Typography variant="body2">{t("dashboard.expiryRisk.noAlternative")}</Typography>}
        </Card>}
        <Alert severity="info"><b>{t("dashboard.expiryRisk.requestReason")}</b> {selected.requestReason ?? "—"}</Alert>
        <Typography variant="caption" color="text.secondary">{t("dashboard.expiryRisk.detectedAt")}: {new Date(selected.detectedAt).toLocaleString()}</Typography>
        {decision.isError && <Alert severity="error">{t("dashboard.expiryRisk.decisionError")}</Alert>}
      </Stack></DialogContent><DialogActions><Button onClick={() => setSelected(null)}>{t("common.actions.close")}</Button>
        {canExecute && ["OPEN", "UNDER_REVIEW"].includes(selected.status) && <><Button color="error" disabled={decision.isPending || !selected.expectedHandlingUnitNumber} onClick={() => decision.mutate({ exception: selected, value: "REJECTED" })}>{t("dashboard.expiryRisk.decisions.swapHu")}</Button><Button variant="contained" disabled={decision.isPending} onClick={() => decision.mutate({ exception: selected, value: "APPROVED" })}>{t("dashboard.expiryRisk.decisions.approveDerogation")}</Button></>}
      </DialogActions></>}
    </Dialog>
  </Fragment>;
}
